<template>
  <div id="app" :lang="bodyLang" :style="styles">
    <div v-if="customizeLoading" class="app-loading">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48" fill="#e6edf3">
        <rect x="0" y="0" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0s" calcMode="discrete"/></rect>
        <rect x="17" y="0" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.125s" calcMode="discrete"/></rect>
        <rect x="34" y="0" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.25s" calcMode="discrete"/></rect>
        <rect x="0" y="17" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.875s" calcMode="discrete"/></rect>
        <rect x="34" y="17" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.375s" calcMode="discrete"/></rect>
        <rect x="0" y="34" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.75s" calcMode="discrete"/></rect>
        <rect x="17" y="34" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.625s" calcMode="discrete"/></rect>
        <rect x="34" y="34" width="14" height="14"><animate attributeName="fill" values="#3683D8;#e6edf3;#e6edf3" keyTimes="0;0.125;1" dur="1s" repeatCount="indefinite" begin="0.5s" calcMode="discrete"/></rect>
      </svg>
    </div>
    <router-view v-else/>
  </div>
</template>

<script>
import isMobile from 'ismobilejs';

import { mapActions, mapState } from 'vuex';
import { LANG, LangValues } from './const/Lang';
import CookieKeys from '@/const/CookieKeys';
import MomentLang from '@/infra/lang/MomentLang';
import Merengue from '@/infra/api/Merengue';
import Utility from '@/mixins/Utility';
import { fetchNicherMapConfig } from '@/infra/bandiera/BandieraClient';
import { adaptNicherMapConfigToCustomize } from '@/infra/bandiera/bandieraCustomizeAdapter';

export default {
  name: 'app',
  data() {
    return {
      td: null,
      bodyLang: 'ja',
      customizeLoading: true,
    };
  },
  head: {
    link: function () {
      let favicon;
      // favicon で参照するパスを設定
      if (this.customize.providesForNtj && this.customize.overseasSite && this.customize.overseasSite.languages &&
        this.customize.overseasSite.languages.some(language => language.toLowerCase() === this.bodyLang)) {
        // 海外向けサイト (Japan Travel)
        favicon = this.isMobile
          ? Utility.methods.uGetStaticFileUrl(this.customize.overseasSite.favicon.sp)
          : Utility.methods.uGetStaticFileUrl(this.customize.overseasSite.favicon.pc);
      } else {
        // ニッチャートラベルのfavicon
        favicon = "https://cdn.prod.website-files.com/626278e900e5cb9961f31b4d/62a2e003de62994d45fb3340_favicon_32x32.png";
      }
      return [
        {rel: 'shortcut icon', href: favicon},
        {rel: 'icon', href: favicon},
      ];
    },
    meta: function() {
      return [
        {
          name: 'description',
          content: this.customize.description
        },
        {
          name: 'robots',
          content: !this.customize.indexPages || this.customize.indexPages.includes(this.$route.name) ? 'all' : 'none',
          id: 'meta-robots',
        },
      ];
    },
  },
  computed: {
    ...mapState('system', {
      customize: 'customize',
      isMobile: 'isMobile',
    }),
    styles() {
      return {
        '--primary-color': this.customize.primaryColor,
        '--secondary-color': this.customize.secondaryColor,
        '--route-color': this.customize.routeColor,
        '--accent-color': this.customize.accentColor,
        '--guidemap-primary-color': this.customize.primaryColor,
        '--guidemap-secondary-color': this.customize.secondaryColor,
        '--guidemap-focus-color': `rgba(${this.customize.primaryColorRgb},0.1)`,
      };
    },
  },
  created() {
    this.$router.onReady(async () => {
      if (this.$route.name === 'ErrorPage' || !this.$route.name) {
        // ErrorPage ルート、または * catchall（/access-denied 等）はカスタマイズデータ取得をスキップ
        this.customizeLoading = false;
        return;
      }
      try {
        const routeLang = String(this.$route.params.lang || LANG.ja).toLowerCase();
        // 言語チェック
        const paramLang = LangValues.find((lang) => {
          return routeLang === lang.path;
        });
        let currentLang = paramLang;
        if (!currentLang) {
          currentLang = LangValues.find((lang) => lang.path === LANG.ja);
        }

        const uri = new URL(window.location.href);
        const originalDomain = Object.values(process.env.ORIGINAL_DOMAIN_LIST).find((domain) => domain.DOMAIN === uri.hostname);
        const siteId = originalDomain ? originalDomain.SID : this.$route.params.siteId;
        this.setSiteId(siteId);
        // Bandiera tenantId は siteId そのもの。
        // 旧URL構造 (/town-guide/<lang>/<categoryName>) を使っていた頃は categoryName が
        // 実際の tenantId だったが、新URL構造 (/<categoryName>/<lang>/guide-map/) では
        // categoryName が siteId として扱われるため、siteId をそのまま tenantId とする。
        const tenantId = siteId;
        // カスタマイズデータを非同期で取得
        const customizeData = await this.getCustomizeData(tenantId, routeLang);
        if (!customizeData) {
          return;
        }
        this.setCustomize({data: customizeData, lang: currentLang.path});

        // 設定言語がない場合、ブラウザ言語をチェック
        if (!this.$route.params.lang) {
          currentLang = LangValues.find((lang) => lang.path === this.fetchBrowserLang());
        }
        const i18nLang = currentLang?.path || LANG.ja;
        // stateとローカライズ用のmoduleへ言語を指定
        this.bodyLang = i18nLang;
        this.lang(i18nLang);
        this.$i18n.locale = i18nLang;
        MomentLang.setMomentLang(currentLang?.momentLang || LANG.ja);

        // カスタマイズデータから判定系
        await this.transitionPage();

        // 大きさからSP判定を実施
        this.setMobile(isMobile.any);

        // cookieのseq値有無チェック
        if (!this.$cookies.isKey(CookieKeys.PLANNING_COM_SEQ)) {
          // seq値を発行しCookieにセット
          const response = await new Merengue().getSeq(siteId, currentLang.path);
          this.$cookies.set(CookieKeys.PLANNING_COM_SEQ, response.data, '40y');
        }
        // pkg ログインチェック
        if (this.$route.query.p && this.$route.query.t) {
          this.$cookies.set(CookieKeys.SP_PKG_LOGIN, `${this.$route.query.p}.${this.$route.query.t}`, '30d');
        }
      } finally {
        this.customizeLoading = false;
      }
    });
  },
  methods: {
    ...mapActions('lang', [
      'lang',
    ]),
    ...mapActions('system', {
      setMobile: 'isMobile',
      setCustomize: 'setCustomize',
      setSiteId: 'setSiteId',
      isPageRequiredLogin: 'isPageRequiredLogin',
      setPageAccessErrorDialog: 'setPageAccessErrorDialog',
      setPageAccessErrorDialogContents: 'setPageAccessErrorDialogContents',
      isAuthenticated: 'isAuthenticated',
    }),
    /**
     * ページ遷移時の処理
     */
    async transitionPage() {
      // カスタマイズデータ読み込み完了前はスキップ（onReadyから明示的に呼ばれる）
      if (this.customizeLoading) {
        return;
      }
      // 各ページへのアクセス判定
      if (!this.permittedPageAccess()) {
        // アクセス不可のページのためリダイレクトページへ遷移
        this.$router.push({ name: 'RedirectPage', params: this.$route.params, query: this.$route.query });
      }

      // タイトル設定
      document.title = this.getPageTitle();

      // ログイン必須画面判定
      const isAuth = await this.isAuthenticated();
      if (await this.isPageRequiredLogin(this.$route.name) && !isAuth) {
        // ログイン必須ページだがログインしていないためエラーモーダル表示
        this.setPageAccessErrorDialog(true);
        this.setPageAccessErrorDialogContents({title: 'ログインしてください', text: 'ログインが必要な画面です。<br/>ログイン後再度アクセスしてください。'});
      } else {
        this.setPageAccessErrorDialog(false);
      }
    },
    /**
     * Bandiera CMS から NicherMapConfig を取得し、customize 形式に変換して返します。
     * 旧: Merengue /{siteId}/{lang}/site/customize/get を置き換えます。
     *
     * @param {string} siteId  サイトID（tenantId として使用）
     * @param {string} lang    ロケール
     */
    async getCustomizeData(siteId, lang) {
      try {
        // siteId を tenantId として使用する
        // TODO: siteId と Bandiera tenantId の対応が異なる場合はマッピングテーブルの追加を検討すること
        const nicherMapConfig = await fetchNicherMapConfig(siteId, lang);

        if (!nicherMapConfig) {
          // 未登録 tenant または空レスポンスの場合
          console.error('[getCustomizeData] NicherMapConfig が取得できませんでした (データなし):', { siteId, lang });
          this.$router.push({ name: 'ErrorPage', query: this.$route.query });
          return null;
        }

        return adaptNicherMapConfigToCustomize(nicherMapConfig, lang);
      } catch (error) {
        const apiError = error;
        if (apiError.type === 'unauthorized' || apiError.type === 'forbidden') {
          // 401 / 403: 認証エラー → ログインページへリダイレクト or エラーページ
          console.error('[getCustomizeData] Bandiera 認証エラー:', { siteId, lang, error });
        } else if (apiError.type === 'not_found') {
          // 404: テナント未登録
          console.error('[getCustomizeData] テナントが Bandiera に登録されていません:', { siteId, lang });
        } else {
          console.error('[getCustomizeData] Bandiera API エラー:', { siteId, lang, error });
        }
        this.$router.push({ name: 'ErrorPage', query: this.$route.query });
        return null;
      }
    },
    permittedPageAccess() {
      const pageName = this.$route.name;
      if (!pageName || pageName === 'ErrorPage' || pageName === 'RedirectPage') {
        // エラーページ・リダイレクトページは判定させない
        return true;
      } else if (!this.customize.accessiblePages) {
        // 設定がない場合は全てのページ許可
        return true;
      } else if (this.customize.accessiblePages.some((page) => pageName === page)) {
        // カスタマイズデータに画面のアクセス設定を持っている場合、設定されているページのみアクセス可
        return true;
      } else {
        return false;
      }
    },
    /**
     * INDEXを設定するページかどうか（カスタマイズ）
     */
    isIndexPage() {
      return this.customize.indexPages && this.customize.indexPages.some((page) => this.$route.name === page);
    },
    /**
     * ページごとに設定するタイトルがあれば取得（カスタマイズ）
     */
    getPageTitle() {
      try {
        const title = this.customize.pagesTitle.find((target) => this.$route.name === target.page).title;
        return `${title} | ${this.customize.title}`;
      } catch (error) {
        return this.customize.title;
      }
    },
    /**
     * ブラウザの言語を取得します
     */
    fetchBrowserLang() {
      const usableLang = this.customize.langList;
      const browserLang = (window.navigator.languages && window.navigator.languages[0]) ||
          window.navigator.language ||
          window.navigator.userLanguage ||
          window.navigator.browserLanguage;
      return browserLang.includes(LANG.en) && usableLang.includes(LANG.en) ? LANG.en
        : new RegExp(LANG.ja, 'i').test(browserLang) && usableLang.includes(LANG.ja) ? LANG.ja
        : new RegExp(LANG.zhTW, 'i').test(browserLang) && usableLang.includes(LANG.zhTW) ? LANG.zhTW
        : new RegExp(LANG.zhCN, 'i').test(browserLang) && usableLang.includes(LANG.zhCN) ? LANG.zhCN
        : new RegExp(LANG.ko, 'i').test(browserLang) && usableLang.includes(LANG.ko) ? LANG.ko
        : new RegExp(LANG.th, 'i').test(browserLang) && usableLang.includes(LANG.th) ? LANG.th
        : LANG.ja;
    },
    hex2rgb(hex) {
      if (hex.slice(0, 1) === '#') hex = hex.slice(1);
      if (hex.length === 3) hex = hex.slice(0, 1) + hex.slice(0, 1) + hex.slice(1, 2) + hex.slice(1, 2) + hex.slice(2, 3) + hex.slice(2, 3);
      return [ hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6) ].map(function (str) {
        return parseInt(str, 16);
      }).join(',');
    }
  },
  watch: {
    $route: 'transitionPage',
  },
};
</script>

<style lang="scss">
@import './assets/fonts/font.css';


#app {
  font-family: YakuHanJP, "Zen Kaku Gothic New", sans-serif;
  font-feature-settings: "palt";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.5px;
  font-weight: 400;
}

#app .app-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: #fff;
}

body {
  margin: 0;
  /* カスタマイズカラー デフォルト値 */
  --primary-color: #fbbe87;
  --secondary-color: #EDF5FF;
  --route-color: #fbbe87;
  --accent-color: #E67373;
}

// TODO input要素フォーカス時の色を#fbbe87変更する
input:focus {
  outline: none;
  border-color: rgba(var(--color_rgb), .5);
}

ul, ol {
  list-style: none;
  margin: 0;
  padding: 0;
}

*:focus {
  outline: none;
}

.ntjpin-div {
  overflow: visible;
}

.ntjpin-div:hover .pin-tooltip {
  display: inline-block;
}

#app .overlay.overlay--active:before {
  z-index: 10000;
}

#app .dialog__content .dialog--active {
  z-index: 10001;
}

#app .ntgeo-parts.ntgeo-copyright {
  z-index: 3;
  bottom: 3px;
  left: 5px;
  transform: unset;
}
#app {
  #sp-map {
    .ntgeo-parts.ntgeo-copyright {
      margin-right: 46px;
    }
  }
}

/* IE 対策 */
@media (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #app .ntgeo-parts.ntgeo-copyright {
    transform: translateX(0%);
  }
}

#app .application.theme--light {
  background-color: #fff;
}

.fade-enter-active {
  transition: opacity .2s ease .2s;
}

.fade-leave-active {
  transition: opacity .2s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.leftin-enter-active {
  transition: all .2s ease-out .2s;
}

.leftin-leave-active {
  transition: all .2s ease-out;
}

.leftin-enter, .leftin-leave-to {
  transform: translateX(-100%);
}

.rightin-enter-active {
  transition: all .2s ease-out .2s;
}

.rightin-leave-active {
  transition: all .2s ease-out;
}

.rightin-enter, .rightin-leave-to {
  transform: translateX(100%);
}

.vertical-separator {
  width: 1px;
  height: 100%;
  border-left: 1px solid lightgray;
  margin: 0;
}
.horizontal-separator {
  width: 100%;
  height: 1px;
  border-top: 1px solid lightgray;
  margin: 0;
}

/**
 * ガイドマップ カスタムプロパティ
 */
:root {
  --guidemap-primary-text-color: #222;
  --guidemap-secondary-text-color: #808080;
  --guidemap-divider-color: #F2F2F2;
  --guidemap-primary-color: ##fbbe87; // デフォルト
  --guidemap-secondary-color: #D1FFD3; // デフォルト
  --guidemap-focus-color: #F2FFF1;
}

// テンプレートモーダルでbodyにappendしている用のスタイル
.plan-modal-template-under-body.el-dialog {
  border-radius: 6px;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.36);
}
.plan-modal-template-under-body {
  .el-dialog {
    &__header {
      padding: 32px 58px;
      @media screen and (max-width: 430px) {
        padding: 32px 52px;
      }
    }
    &__title {
      color: #000000;
      font-size: 20px;
      font-weight: bold;
      line-height: inherit;
    }
    &__headerbtn {
      width: 20px;
      height: 20px;
      top: 34px;
      right: 34px;
    }
    &__close {
      color: #000000;
      font-size: 20px;
      font-weight: bold;
      width: 20px;
      height: 20px;
      margin: 0;
    }
    &__footer {
      border-top: 1px solid #666666;
      padding: 32px 58px;
    }
  }
}
</style>
